import {
  createParser,
  type LiteralNode,
  type LiteralRule,
  type NonTerminalNode,
  type NonTerminalRule,
  type TerminalNode,
  type TerminalRule,
} from "@theprelude/lalr-parser";
import { ODataToken, ODataTokenType } from "./lexer";

// OData Filter Grammar Rules for LALR Parser
// Based on OData v4.01 ABNF specification
// Using strictly typed children arrays containing only AST nodes

// AST Node Type Definitions
export interface BoolCommonExprNode extends NonTerminalNode<"boolCommonExpr"> {
  children: [CommonExprNode];
}

export interface CommonExprNode extends NonTerminalNode<"commonExpr"> {
  children: [OrExprNode] | [CommonExprNode, OrOpNode, OrExprNode];
}

export interface OrExprNode extends NonTerminalNode<"orExpr"> {
  children: [AndExprNode] | [OrExprNode, AndOpNode, AndExprNode];
}

export interface AndExprNode extends NonTerminalNode<"andExpr"> {
  children: [ComparisonExprNode] | [AndExprNode, AndOpNode, ComparisonExprNode];
}

export interface ComparisonExprNode extends NonTerminalNode<"comparisonExpr"> {
  children:
    | [ArithmeticExprNode]
    | [ArithmeticExprNode, ComparisonOpNode, ArithmeticExprNode];
}

export interface ArithmeticExprNode extends NonTerminalNode<"arithmeticExpr"> {
  children:
    | [MultiplicativeExprNode]
    | [ArithmeticExprNode, AdditiveOpNode, MultiplicativeExprNode];
}

export interface MultiplicativeExprNode
  extends NonTerminalNode<"multiplicativeExpr"> {
  children:
    | [UnaryExprNode]
    | [MultiplicativeExprNode, MultiplicativeOpNode, UnaryExprNode];
}

export interface UnaryExprNode extends NonTerminalNode<"unaryExpr"> {
  children: [PrimaryExprNode] | [UnaryOpNode, UnaryExprNode];
}

export interface PrimaryExprNode extends NonTerminalNode<"primaryExpr"> {
  children:
    | [ValueLiteralNode]
    | [MemberExprNode]
    | [MethodCallNode]
    | [ParenOrListExprNode]
    | [LambdaExprNode];
}

export interface MemberExprNode extends NonTerminalNode<"memberExpr"> {
  children: [IdentifierNode] | [MemberExprNode, SlashNode, IdentifierNode];
}

export interface MethodCallNode extends NonTerminalNode<"methodCall"> {
  children:
    | [MethodNameNode, OpenParenNode, CloseParenNode] // no args (e.g., now())
    | [MethodNameNode, OpenParenNode, CommonExprNode, CloseParenNode] // single arg
    | [
        MethodNameNode,
        OpenParenNode,
        CommonExprNode,
        CommaNode,
        CommonExprNode,
        CloseParenNode,
      ] // two args
    | [
        MethodNameNode,
        OpenParenNode,
        CommonExprNode,
        CommaNode,
        CommonExprNode,
        CommaNode,
        CommonExprNode,
        CloseParenNode,
      ]; // three args
}

export interface LambdaExprNode extends NonTerminalNode<"lambdaExpr"> {
  children:
    | [
        MemberExprNode,
        SlashNode,
        AnyNode,
        OpenParenNode,
        LambdaPredicateNode,
        CloseParenNode,
      ]
    | [
        MemberExprNode,
        SlashNode,
        AllNode,
        OpenParenNode,
        LambdaPredicateNode,
        CloseParenNode,
      ];
}

export interface LambdaPredicateNode
  extends NonTerminalNode<"lambdaPredicate"> {
  children: [IdentifierNode, ColonNode, CommonExprNode];
}

export interface ParenOrListExprNode
  extends NonTerminalNode<"parenOrListExpr"> {
  children:
    | [OpenParenNode, CommonExprNode, CloseParenNode]
    | [OpenParenNode, ListItemsNode, CommaNode, CommonExprNode, CloseParenNode]
    | [OpenParenNode, ListItemsNode, CloseParenNode]
    | [OpenParenNode, CloseParenNode];
}

export interface ListItemsNode extends NonTerminalNode<"listItems"> {
  children: [CommonExprNode] | [ListItemsNode, CommaNode, CommonExprNode];
}

export interface ValueLiteralNode extends NonTerminalNode<"valueLiteral"> {
  children:
    | [StringLiteralNode]
    | [NumberLiteralNode]
    | [BooleanLiteralNode]
    | [NullLiteralNode]
    | [DurationLiteralNode];
}

// Terminal nodes for literals
export interface StringLiteralNode extends TerminalNode<"stringLiteral"> {
  value: string;
}

export interface NumberLiteralNode extends TerminalNode<"numberLiteral"> {
  value: number;
}

export interface BooleanLiteralNode extends TerminalNode<"booleanLiteral"> {
  value: boolean;
}

export interface NullLiteralNode extends TerminalNode<"nullLiteral"> {
  value: null;
}

export interface DurationLiteralNode extends TerminalNode<"durationLiteral"> {
  value: string; // ISO 8601 duration string like "P3DT0H0M0S"
}

export interface IdentifierNode extends TerminalNode<"identifier"> {
  value: string;
}

export interface MethodNameNode extends TerminalNode<"methodName"> {
  value: string;
}

// Literal token nodes (punctuation and operators)
export interface AndOpNode extends LiteralNode<"and"> {}
export interface OrOpNode extends LiteralNode<"or"> {}
export interface ComparisonOpNode
  extends LiteralNode<"eq" | "ne" | "lt" | "le" | "gt" | "ge" | "in" | "has"> {}
export interface AdditiveOpNode extends LiteralNode<"add" | "sub"> {}
export interface MultiplicativeOpNode
  extends LiteralNode<"mul" | "div" | "divby" | "mod"> {}
export interface UnaryOpNode extends LiteralNode<"not" | "minus"> {}
export interface OpenParenNode extends LiteralNode<"("> {}
export interface CloseParenNode extends LiteralNode<")"> {}
export interface CommaNode extends LiteralNode<","> {}
export interface SlashNode extends LiteralNode<"/"> {}
export interface ColonNode extends LiteralNode<":"> {}
export interface AnyNode extends LiteralNode<"any"> {}
export interface AllNode extends LiteralNode<"all"> {}

// Grammar Rules

// 1. Boolean expressions (entry point)
const boolFromCommonRule = {
  name: "boolCommonExpr",
  symbols: ["commonExpr"],
  handler: (expr: CommonExprNode): BoolCommonExprNode => ({
    type: "boolCommonExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 2. Common expressions with OR operators (lowest precedence)
const orExprRule = {
  name: "commonExpr",
  symbols: ["commonExpr", "or", "orExpr"],
  handler: (
    left: CommonExprNode,
    op: OrOpNode,
    right: OrExprNode,
  ): CommonExprNode => ({
    type: "commonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const commonFromOrRule = {
  name: "commonExpr",
  symbols: ["orExpr"],
  handler: (expr: OrExprNode): CommonExprNode => ({
    type: "commonExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 3. OR expressions with AND operators (higher precedence than OR)
const andExprRule = {
  name: "orExpr",
  symbols: ["orExpr", "and", "andExpr"],
  handler: (
    left: OrExprNode,
    op: AndOpNode,
    right: AndExprNode,
  ): OrExprNode => ({
    type: "orExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const orFromAndRule = {
  name: "orExpr",
  symbols: ["andExpr"],
  handler: (expr: AndExprNode): OrExprNode => ({
    type: "orExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 4. AND expressions
const andFromComparisonRule = {
  name: "andExpr",
  symbols: ["comparisonExpr"],
  handler: (expr: ComparisonExprNode): AndExprNode => ({
    type: "andExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 5. Comparison expressions
const eqExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "eq", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const neExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "ne", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const ltExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "lt", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const leExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "le", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const gtExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "gt", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const geExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "ge", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const inExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "in", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const hasExprRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr", "has", "arithmeticExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: ComparisonOpNode,
    right: ArithmeticExprNode,
  ): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const comparisonFromArithmeticRule = {
  name: "comparisonExpr",
  symbols: ["arithmeticExpr"],
  handler: (expr: ArithmeticExprNode): ComparisonExprNode => ({
    type: "comparisonExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 6. Arithmetic expressions (additive: lower precedence)
const addExprRule = {
  name: "arithmeticExpr",
  symbols: ["arithmeticExpr", "add", "multiplicativeExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: AdditiveOpNode,
    right: MultiplicativeExprNode,
  ): ArithmeticExprNode => ({
    type: "arithmeticExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const subExprRule = {
  name: "arithmeticExpr",
  symbols: ["arithmeticExpr", "sub", "multiplicativeExpr"],
  handler: (
    left: ArithmeticExprNode,
    op: AdditiveOpNode,
    right: MultiplicativeExprNode,
  ): ArithmeticExprNode => ({
    type: "arithmeticExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const arithmeticFromMultiplicativeRule = {
  name: "arithmeticExpr",
  symbols: ["multiplicativeExpr"],
  handler: (expr: MultiplicativeExprNode): ArithmeticExprNode => ({
    type: "arithmeticExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 7. Multiplicative expressions (higher precedence)
const mulExprRule = {
  name: "multiplicativeExpr",
  symbols: ["multiplicativeExpr", "mul", "unaryExpr"],
  handler: (
    left: MultiplicativeExprNode,
    op: MultiplicativeOpNode,
    right: UnaryExprNode,
  ): MultiplicativeExprNode => ({
    type: "multiplicativeExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const divExprRule = {
  name: "multiplicativeExpr",
  symbols: ["multiplicativeExpr", "div", "unaryExpr"],
  handler: (
    left: MultiplicativeExprNode,
    op: MultiplicativeOpNode,
    right: UnaryExprNode,
  ): MultiplicativeExprNode => ({
    type: "multiplicativeExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const divbyExprRule = {
  name: "multiplicativeExpr",
  symbols: ["multiplicativeExpr", "divby", "unaryExpr"],
  handler: (
    left: MultiplicativeExprNode,
    op: MultiplicativeOpNode,
    right: UnaryExprNode,
  ): MultiplicativeExprNode => ({
    type: "multiplicativeExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const modExprRule = {
  name: "multiplicativeExpr",
  symbols: ["multiplicativeExpr", "mod", "unaryExpr"],
  handler: (
    left: MultiplicativeExprNode,
    op: MultiplicativeOpNode,
    right: UnaryExprNode,
  ): MultiplicativeExprNode => ({
    type: "multiplicativeExpr",
    children: [left, op, right],
  }),
} as const satisfies NonTerminalRule;

const multiplicativeFromUnaryRule = {
  name: "multiplicativeExpr",
  symbols: ["unaryExpr"],
  handler: (expr: UnaryExprNode): MultiplicativeExprNode => ({
    type: "multiplicativeExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

// 8. Unary expressions
const notExprRule = {
  name: "unaryExpr",
  symbols: ["not", "unaryExpr"],
  handler: (op: UnaryOpNode, expr: UnaryExprNode): UnaryExprNode => ({
    type: "unaryExpr",
    children: [op, expr],
  }),
} as const satisfies NonTerminalRule;

const negateExprRule = {
  name: "unaryExpr",
  symbols: ["minus", "unaryExpr"],
  handler: (op: UnaryOpNode, expr: UnaryExprNode): UnaryExprNode => ({
    type: "unaryExpr",
    children: [op, expr],
  }),
} as const satisfies NonTerminalRule;

const unaryFromPrimaryRule = {
  name: "unaryExpr",
  symbols: ["primaryExpr"],
  handler: (expr: PrimaryExprNode): UnaryExprNode => ({
    type: "unaryExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

const parenOrListExprRule = {
  name: "primaryExpr",
  symbols: ["parenOrListExpr"],
  handler: (expr: ParenOrListExprNode): PrimaryExprNode => ({
    type: "primaryExpr",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

const parenCommonExprRule = {
  name: "parenOrListExpr",
  symbols: ["(", "commonExpr", ")"],
  handler: (
    open: OpenParenNode,
    expr: CommonExprNode,
    close: CloseParenNode,
  ): ParenOrListExprNode => ({
    type: "parenOrListExpr",
    children: [open, expr, close],
  }),
} as const satisfies NonTerminalRule;

const parenListMultiItemRule = {
  name: "parenOrListExpr",
  symbols: ["(", "listItems", ",", "commonExpr", ")"],
  handler: (
    open: OpenParenNode,
    items: ListItemsNode,
    comma: CommaNode,
    expr: CommonExprNode,
    close: CloseParenNode,
  ): ParenOrListExprNode => ({
    type: "parenOrListExpr",
    children: [open, items, comma, expr, close],
  }),
} as const satisfies NonTerminalRule;

const parenListTrailingCommaRule = {
  name: "parenOrListExpr",
  symbols: ["(", "listItems", ",", ")"],
  handler: (
    open: OpenParenNode,
    items: ListItemsNode,
    comma: CommaNode,
    close: CloseParenNode,
  ): ParenOrListExprNode => ({
    type: "parenOrListExpr",
    children: [open, items, close],
  }),
} as const satisfies NonTerminalRule;

const parenListEmptyRule = {
  name: "parenOrListExpr",
  symbols: ["(", ")"],
  handler: (
    open: OpenParenNode,
    close: CloseParenNode,
  ): ParenOrListExprNode => ({
    type: "parenOrListExpr",
    children: [open, close],
  }),
} as const satisfies NonTerminalRule;

const parenListEmptyTrailingCommaRule = {
  name: "parenOrListExpr",
  symbols: ["(", ",", ")"],
  handler: (
    open: OpenParenNode,
    comma: CommaNode,
    close: CloseParenNode,
  ): ParenOrListExprNode => ({
    type: "parenOrListExpr",
    children: [open, close],
  }),
} as const satisfies NonTerminalRule;

const listItemsRule = {
  name: "listItems",
  symbols: ["commonExpr"],
  handler: (expr: CommonExprNode): ListItemsNode => ({
    type: "listItems",
    children: [expr],
  }),
} as const satisfies NonTerminalRule;

const listItemsRecursiveRule = {
  name: "listItems",
  symbols: ["listItems", ",", "commonExpr"],
  handler: (
    items: ListItemsNode,
    comma: CommaNode,
    expr: CommonExprNode,
  ): ListItemsNode => ({
    type: "listItems",
    children: [items, comma, expr],
  }),
} as const satisfies NonTerminalRule;

// 7. Member expressions (property paths)
const memberExprRule = {
  name: "primaryExpr",
  symbols: ["memberExpr"],
  handler: (member: MemberExprNode): PrimaryExprNode => ({
    type: "primaryExpr",
    children: [member],
  }),
} as const satisfies NonTerminalRule;

const simpleMemberRule = {
  name: "memberExpr",
  symbols: ["identifier"],
  handler: (id: IdentifierNode): MemberExprNode => ({
    type: "memberExpr",
    children: [id],
  }),
} as const satisfies NonTerminalRule;

// Property path navigation (e.g., "Address/City" or lambda variable paths like "instances/last_seen")
const nestedMemberRule = {
  name: "memberExpr",
  symbols: ["memberExpr", "/", "identifier"],
  handler: (
    parent: MemberExprNode,
    slash: SlashNode,
    property: IdentifierNode,
  ): MemberExprNode => ({
    type: "memberExpr",
    children: [parent, slash, property],
  }),
} as const satisfies NonTerminalRule;

// 8. Lambda expressions
const lambdaExprRule = {
  name: "primaryExpr",
  symbols: ["lambdaExpr"],
  handler: (lambda: LambdaExprNode): PrimaryExprNode => ({
    type: "primaryExpr",
    children: [lambda],
  }),
} as const satisfies NonTerminalRule;

const anyLambdaRule = {
  name: "lambdaExpr",
  symbols: ["memberExpr", "/", "any", "(", "lambdaPredicate", ")"],
  handler: (
    member: MemberExprNode,
    slash: SlashNode,
    any: AnyNode,
    open: OpenParenNode,
    predicate: LambdaPredicateNode,
    close: CloseParenNode,
  ): LambdaExprNode => ({
    type: "lambdaExpr",
    children: [member, slash, any, open, predicate, close],
  }),
} as const satisfies NonTerminalRule;

const allLambdaRule = {
  name: "lambdaExpr",
  symbols: ["memberExpr", "/", "all", "(", "lambdaPredicate", ")"],
  handler: (
    member: MemberExprNode,
    slash: SlashNode,
    all: AllNode,
    open: OpenParenNode,
    predicate: LambdaPredicateNode,
    close: CloseParenNode,
  ): LambdaExprNode => ({
    type: "lambdaExpr",
    children: [member, slash, all, open, predicate, close],
  }),
} as const satisfies NonTerminalRule;

const lambdaPredicateRule = {
  name: "lambdaPredicate",
  symbols: ["identifier", ":", "commonExpr"],
  handler: (
    variable: IdentifierNode,
    colon: ColonNode,
    expr: CommonExprNode,
  ): LambdaPredicateNode => ({
    type: "lambdaPredicate",
    children: [variable, colon, expr],
  }),
} as const satisfies NonTerminalRule;

// 9. Method calls (updated to include no-arg methods like now())
const methodCallExprRule = {
  name: "primaryExpr",
  symbols: ["methodCall"],
  handler: (method: MethodCallNode): PrimaryExprNode => ({
    type: "primaryExpr",
    children: [method],
  }),
} as const satisfies NonTerminalRule;

// No argument methods (e.g., now())
const noArgMethodRule = {
  name: "methodCall",
  symbols: ["methodName", "(", ")"],
  handler: (
    method: MethodNameNode,
    open: OpenParenNode,
    close: CloseParenNode,
  ): MethodCallNode => ({
    type: "methodCall",
    children: [method, open, close],
  }),
} as const satisfies NonTerminalRule;

// Single argument methods (e.g., length, tolower, toupper, trim)
const singleArgMethodRule = {
  name: "methodCall",
  symbols: ["methodName", "(", "commonExpr", ")"],
  handler: (
    method: MethodNameNode,
    open: OpenParenNode,
    arg: CommonExprNode,
    close: CloseParenNode,
  ): MethodCallNode => ({
    type: "methodCall",
    children: [method, open, arg, close],
  }),
} as const satisfies NonTerminalRule;

// Two argument methods (e.g., contains, startswith, endswith, indexof)
const twoArgMethodRule = {
  name: "methodCall",
  symbols: ["methodName", "(", "commonExpr", ",", "commonExpr", ")"],
  handler: (
    method: MethodNameNode,
    open: OpenParenNode,
    arg1: CommonExprNode,
    comma: CommaNode,
    arg2: CommonExprNode,
    close: CloseParenNode,
  ): MethodCallNode => ({
    type: "methodCall",
    children: [method, open, arg1, comma, arg2, close],
  }),
} as const satisfies NonTerminalRule;

// Three argument methods (e.g., substring with optional length)
const threeArgMethodRule = {
  name: "methodCall",
  symbols: [
    "methodName",
    "(",
    "commonExpr",
    ",",
    "commonExpr",
    ",",
    "commonExpr",
    ")",
  ],
  handler: (
    method: MethodNameNode,
    open: OpenParenNode,
    arg1: CommonExprNode,
    comma1: CommaNode,
    arg2: CommonExprNode,
    comma2: CommaNode,
    arg3: CommonExprNode,
    close: CloseParenNode,
  ): MethodCallNode => ({
    type: "methodCall",
    children: [method, open, arg1, comma1, arg2, comma2, arg3, close],
  }),
} as const satisfies NonTerminalRule;

// 10. Value literals
const valueLiteralRule = {
  name: "primaryExpr",
  symbols: ["valueLiteral"],
  handler: (literal: ValueLiteralNode): PrimaryExprNode => ({
    type: "primaryExpr",
    children: [literal],
  }),
} as const satisfies NonTerminalRule;

const stringLiteralRule = {
  name: "valueLiteral",
  symbols: ["stringLiteral"],
  handler: (token: StringLiteralNode): ValueLiteralNode => ({
    type: "valueLiteral",
    children: [token],
  }),
} as const satisfies NonTerminalRule;

const numberLiteralRule = {
  name: "valueLiteral",
  symbols: ["numberLiteral"],
  handler: (token: NumberLiteralNode): ValueLiteralNode => ({
    type: "valueLiteral",
    children: [token],
  }),
} as const satisfies NonTerminalRule;

const booleanLiteralRule = {
  name: "valueLiteral",
  symbols: ["booleanLiteral"],
  handler: (token: BooleanLiteralNode): ValueLiteralNode => ({
    type: "valueLiteral",
    children: [token],
  }),
} as const satisfies NonTerminalRule;

const nullLiteralRule = {
  name: "valueLiteral",
  symbols: ["nullLiteral"],
  handler: (token: NullLiteralNode): ValueLiteralNode => ({
    type: "valueLiteral",
    children: [token],
  }),
} as const satisfies NonTerminalRule;

const durationLiteralRule = {
  name: "valueLiteral",
  symbols: ["durationLiteral"],
  handler: (token: DurationLiteralNode): ValueLiteralNode => ({
    type: "valueLiteral",
    children: [token],
  }),
} as const satisfies NonTerminalRule;

// 11. Terminal rules for tokens
const stringLiteralTerminalRule = {
  type: "terminal" as const,
  name: "stringLiteral",
  handler: (token: ODataToken<"stringLiteral">): StringLiteralNode => ({
    type: "stringLiteral",
    token,
    value: token.text.slice(1, -1).replaceAll("''", "'"), // Remove quotes and handle escaped quotes
  }),
} as const satisfies TerminalRule;

const numberLiteralTerminalRule = {
  type: "terminal" as const,
  name: "numberLiteral",
  handler: (token: ODataToken<"numberLiteral">): NumberLiteralNode => ({
    type: "numberLiteral",
    token,
    value: Number.parseFloat(token.text),
  }),
} as const satisfies TerminalRule;

const booleanLiteralTerminalRule = {
  type: "terminal" as const,
  name: "booleanLiteral",
  handler: (token: ODataToken<"booleanLiteral">): BooleanLiteralNode => ({
    type: "booleanLiteral",
    token,
    value: token.text === "true",
  }),
} as const satisfies TerminalRule;

const nullLiteralTerminalRule = {
  type: "terminal" as const,
  name: "nullLiteral",
  handler: (token: ODataToken<"nullLiteral">): NullLiteralNode => ({
    type: "nullLiteral",
    token,
    value: null,
  }),
} as const satisfies TerminalRule;

const durationLiteralTerminalRule = {
  type: "terminal" as const,
  name: "durationLiteral",
  handler: (token: ODataToken<"durationLiteral">): DurationLiteralNode => ({
    type: "durationLiteral",
    token,
    value: token.text.startsWith("duration'")
      ? token.text.slice(9, -1)
      : token.text, // Extract duration string from duration'...'
  }),
} as const satisfies TerminalRule;

const identifierTerminalRule = {
  type: "terminal" as const,
  name: "identifier",
  handler: (token: ODataToken<"identifier">): IdentifierNode => ({
    type: "identifier",
    token,
    value: token.text,
  }),
} as const satisfies TerminalRule;

const methodNameTerminalRule = {
  type: "terminal" as const,
  name: "methodName",
  handler: (token: ODataToken<"methodName">): MethodNameNode => ({
    type: "methodName",
    token,
    value: token.text,
  }),
} as const satisfies TerminalRule;

// 12. Literal rules for operators and punctuation
const literal = <const T extends ODataTokenType>(
  token: ODataToken<T>,
  text: T,
): LiteralNode<T> => {
  if (token.text !== text) {
    throw new Error(
      `Unexpected literal value for token "${text}": "${token.text}"`,
    );
  }
  return { type: text, token, value: text };
};

const andOpRule = {
  type: "literal" as const,
  name: "and",
  handler: (token: ODataToken<"and">): AndOpNode => literal(token, "and"),
} as const satisfies LiteralRule;

const orOpRule = {
  type: "literal" as const,
  name: "or",
  handler: (token: ODataToken<"or">): OrOpNode => literal(token, "or"),
} as const satisfies LiteralRule;

const notOpRule = {
  type: "literal" as const,
  name: "not",
  handler: (token: ODataToken<"not">): UnaryOpNode => literal(token, "not"),
} as const satisfies LiteralRule;

const eqOpRule = {
  type: "literal" as const,
  name: "eq",
  handler: (token: ODataToken<"eq">): ComparisonOpNode => literal(token, "eq"),
} as const satisfies LiteralRule;

const neOpRule = {
  type: "literal" as const,
  name: "ne",
  handler: (token: ODataToken<"ne">): ComparisonOpNode => literal(token, "ne"),
} as const satisfies LiteralRule;

const ltOpRule = {
  type: "literal" as const,
  name: "lt",
  handler: (token: ODataToken<"lt">): ComparisonOpNode => literal(token, "lt"),
} as const satisfies LiteralRule;

const leOpRule = {
  type: "literal" as const,
  name: "le",
  handler: (token: ODataToken<"le">): ComparisonOpNode => literal(token, "le"),
} as const satisfies LiteralRule;

const gtOpRule = {
  type: "literal" as const,
  name: "gt",
  handler: (token: ODataToken<"gt">): ComparisonOpNode => literal(token, "gt"),
} as const satisfies LiteralRule;

const geOpRule = {
  type: "literal" as const,
  name: "ge",
  handler: (token: ODataToken<"ge">): ComparisonOpNode => literal(token, "ge"),
} as const satisfies LiteralRule;

const inOpRule = {
  type: "literal" as const,
  name: "in",
  handler: (token: ODataToken<"in">): ComparisonOpNode => literal(token, "in"),
} as const satisfies LiteralRule;

const hasOpRule = {
  type: "literal" as const,
  name: "has",
  handler: (token: ODataToken<"has">): ComparisonOpNode =>
    literal(token, "has"),
} as const satisfies LiteralRule;

const addOpRule = {
  type: "literal" as const,
  name: "add",
  handler: (token: ODataToken<"add">): AdditiveOpNode => literal(token, "add"),
} as const satisfies LiteralRule;

const subOpRule = {
  type: "literal" as const,
  name: "sub",
  handler: (token: ODataToken<"sub">): AdditiveOpNode => literal(token, "sub"),
} as const satisfies LiteralRule;

const mulOpRule = {
  type: "literal" as const,
  name: "mul",
  handler: (token: ODataToken<"mul">): MultiplicativeOpNode =>
    literal(token, "mul"),
} as const satisfies LiteralRule;

const divOpRule = {
  type: "literal" as const,
  name: "div",
  handler: (token: ODataToken<"div">): MultiplicativeOpNode =>
    literal(token, "div"),
} as const satisfies LiteralRule;

const divbyOpRule = {
  type: "literal" as const,
  name: "divby",
  handler: (token: ODataToken<"divby">): MultiplicativeOpNode =>
    literal(token, "divby"),
} as const satisfies LiteralRule;

const modOpRule = {
  type: "literal" as const,
  name: "mod",
  handler: (token: ODataToken<"mod">): MultiplicativeOpNode =>
    literal(token, "mod"),
} as const satisfies LiteralRule;

const minusOpRule = {
  type: "literal" as const,
  name: "minus",
  handler: (token: ODataToken<"minus">): UnaryOpNode => literal(token, "minus"),
} as const satisfies LiteralRule;

const openParenRule = {
  type: "literal" as const,
  name: "(",
  handler: (token: ODataToken<"(">): OpenParenNode => literal(token, "("),
} as const satisfies LiteralRule;

const closeParenRule = {
  type: "literal" as const,
  name: ")",
  handler: (token: ODataToken<")">): CloseParenNode => literal(token, ")"),
} as const satisfies LiteralRule;

const commaRule = {
  type: "literal" as const,
  name: ",",
  handler: (token: ODataToken<",">): CommaNode => literal(token, ","),
} as const satisfies LiteralRule;

const slashRule = {
  type: "literal" as const,
  name: "/",
  handler: (token: ODataToken<"/">): SlashNode => literal(token, "/"),
} as const satisfies LiteralRule;

const colonRule = {
  type: "literal" as const,
  name: ":",
  handler: (token: ODataToken<":">): ColonNode => literal(token, ":"),
} as const satisfies LiteralRule;

const anyOpRule = {
  type: "literal" as const,
  name: "any",
  handler: (token: ODataToken<"any">): AnyNode => literal(token, "any"),
} as const satisfies LiteralRule;

const allOpRule = {
  type: "literal" as const,
  name: "all",
  handler: (token: ODataToken<"all">): AllNode => literal(token, "all"),
} as const satisfies LiteralRule;

// Export all rules
export const odataFilterRules = [
  boolFromCommonRule,
  orExprRule,
  commonFromOrRule,
  andExprRule,
  orFromAndRule,
  andFromComparisonRule,
  eqExprRule,
  neExprRule,
  ltExprRule,
  leExprRule,
  gtExprRule,
  geExprRule,
  inExprRule,
  hasExprRule,
  comparisonFromArithmeticRule,
  addExprRule,
  subExprRule,
  arithmeticFromMultiplicativeRule,
  mulExprRule,
  divExprRule,
  divbyExprRule,
  modExprRule,
  multiplicativeFromUnaryRule,
  notExprRule,
  negateExprRule,
  unaryFromPrimaryRule,
  parenOrListExprRule,
  parenCommonExprRule,
  parenListMultiItemRule,
  parenListTrailingCommaRule,
  parenListEmptyRule,
  parenListEmptyTrailingCommaRule,
  listItemsRule,
  listItemsRecursiveRule,
  memberExprRule,
  simpleMemberRule,
  nestedMemberRule,
  lambdaExprRule,
  anyLambdaRule,
  allLambdaRule,
  lambdaPredicateRule,
  methodCallExprRule,
  noArgMethodRule,
  singleArgMethodRule,
  twoArgMethodRule,
  threeArgMethodRule,
  valueLiteralRule,
  stringLiteralRule,
  numberLiteralRule,
  booleanLiteralRule,
  nullLiteralRule,
  durationLiteralRule,
  stringLiteralTerminalRule,
  numberLiteralTerminalRule,
  booleanLiteralTerminalRule,
  nullLiteralTerminalRule,
  durationLiteralTerminalRule,
  identifierTerminalRule,
  methodNameTerminalRule,
  andOpRule,
  orOpRule,
  notOpRule,
  eqOpRule,
  neOpRule,
  ltOpRule,
  leOpRule,
  gtOpRule,
  geOpRule,
  inOpRule,
  hasOpRule,
  addOpRule,
  subOpRule,
  mulOpRule,
  divOpRule,
  divbyOpRule,
  modOpRule,
  minusOpRule,
  openParenRule,
  closeParenRule,
  commaRule,
  slashRule,
  colonRule,
  anyOpRule,
  allOpRule,
] as const;

export type ODataNode = ReturnType<
  (typeof odataFilterRules)[number]["handler"]
>;

const parser = createParser(odataFilterRules, "boolCommonExpr");

export const parse = (tokens: ODataToken[]): BoolCommonExprNode =>
  // Since we pass "boolCommonExpr" as the `startSymbol` above, we can
  // safely cast the returned node type to `BoolCommonExprNode` here.
  parser.parse(tokens) as BoolCommonExprNode;
